import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { AlertTriangle, RefreshCw, X } from 'lucide-react';

/**
 * Componente que detecta si el usuario tiene un token JWT obsoleto
 * con role: "company_admin" y lo obliga a cerrar sesión
 */
const TokenMigrationBanner = () => {
  const { user, logout } = useAuth();
  const [show, setShow] = useState(false);
  const [countdown, setCountdown] = useState(10);

  useEffect(() => {
    // Verificar si el usuario tiene un rol obsoleto
    if (user && user.role === 'company_admin') {
      setShow(true);
      
      // Countdown para logout automático
      const timer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            handleForceLogout();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [user]);

  const handleForceLogout = () => {
    // Limpiar todo el localStorage
    localStorage.clear();
    
    // Hacer logout
    logout();
    
    // Recargar página para asegurar limpieza completa
    window.location.href = '/login';
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-8 relative animate-pulse-slow">
        {/* Icono de advertencia */}
        <div className="flex justify-center mb-6">
          <div className="bg-orange-100 rounded-full p-4">
            <AlertTriangle className="w-16 h-16 text-orange-600" />
          </div>
        </div>

        {/* Título */}
        <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">
          🔄 Actualización del Sistema de Permisos
        </h2>

        {/* Mensaje */}
        <div className="space-y-4 text-center mb-6">
          <p className="text-lg text-gray-700">
            Tu sesión utiliza un sistema de roles <strong className="text-orange-600">obsoleto</strong>.
          </p>
          <p className="text-gray-600">
            Hemos actualizado el sistema de permisos para mejorar la seguridad y granularidad de accesos.
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-left">
            <p className="text-sm text-blue-800 font-medium mb-2">
              📋 ¿Qué cambió?
            </p>
            <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
              <li>El rol <code className="bg-blue-200 px-1 rounded">company_admin</code> fue reemplazado</li>
              <li>Ahora usamos permisos <strong>por empresa</strong> (admin, agent, viewer)</li>
              <li>Tus permisos están intactos, solo necesitas actualizar tu sesión</li>
            </ul>
          </div>
        </div>

        {/* Countdown */}
        <div className="bg-orange-100 border-2 border-orange-300 rounded-lg p-4 mb-6">
          <p className="text-center text-orange-800 font-bold text-xl">
            Cerrando sesión automáticamente en <span className="text-3xl">{countdown}</span>s
          </p>
        </div>

        {/* Botones */}
        <div className="flex gap-4">
          <button
            onClick={handleForceLogout}
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 px-6 rounded-lg transition-all transform hover:scale-105 flex items-center justify-center gap-2"
          >
            <RefreshCw className="w-5 h-5" />
            Cerrar Sesión Ahora
          </button>
        </div>

        {/* Nota al pie */}
        <p className="text-xs text-gray-500 text-center mt-4">
          Después de iniciar sesión nuevamente, todos tus permisos funcionarán correctamente.
        </p>
      </div>
    </div>
  );
};

export default TokenMigrationBanner;
